/**
 * Farben und Achsen-Vorgaben für die Diagramme der Seite „Daten".
 *
 * Die Werte sind gegen die weiße Kartenfläche der App geprüft (Helligkeitsband,
 * Chroma-Untergrenze, Farbfehlsichtigkeit, Normalsicht-Abstand). Schlechtestes
 * benachbartes Paar: ΔE 9,2 bei Farbfehlsichtigkeit, 27,6 bei Normalsicht.
 *
 * Eine Auflage: SERIES[2] (`#1baf7a`) erreicht gegen Weiß nur 2,82:1 Kontrast.
 * Wo diese Farbe Bedeutung trägt, braucht es sichtbare Labels oder die
 * Tabellenansicht — Farbe allein genügt dort nicht.
 *
 * Zwei Regeln, die beim Erweitern gelten:
 * - Farbe folgt der Entität, nie ihrem Rang. Ein Filter, der Serien entfernt,
 *   darf die verbleibenden nicht umfärben.
 * - Ab neun Kategorien wird nicht durchrotiert, sondern der Rest zu „Übrige"
 *   gebündelt.
 */

/** Kategoriale Serienfarben, feste Reihenfolge. */
export const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'] as const;

/**
 * Sequenzielle Blau-Rampe für die Belegungs-Heatmap: ein Farbton, hell → dunkel.
 *
 * Geprüft: monotone Helligkeit, ein Farbton (4° Streuung). Die hellste Stufe
 * erreicht gegen Weiß nur 1,32:1 — das ist **beabsichtigt**. Die 2:1-Grenze gilt
 * für ordinale Stufenfolgen (Ränge, Trichter); bei einer sequenziellen Heatmap
 * darf „nahe null" zur Fläche hin zurücktreten. Bitte nicht als Versehen
 * „korrigieren".
 *
 * Folge daraus: „keine Daten" darf **nicht** farblich abgegrenzt werden — der
 * Unterschied zu 0 % wäre nicht wahrnehmbar. Dafür ist `NO_DATA` da: keine
 * Füllung, nur ein gestrichelter Rand.
 */
export const SEQUENTIAL = [
  '#cde2fb', // 0 %
  '#9ec5f4', // 1–20 %
  '#6da7ec', // 21–40 %
  '#2a78d6', // 41–60 %
  '#1c5cab', // 61–80 %
  '#104281', // 81–100 %
] as const;

export const SEQUENTIAL_LABELS = ['0 %', '1–20', '21–40', '41–60', '61–80', '81–100 %'] as const;

/** Ordnet einen Anteil (0–1) einer Stufe zu. `null` → keine Daten. */
export function sequentialStep(share: number | null | undefined): string | null {
  if (share == null) return null;
  const pct = share * 100;
  if (pct <= 0) return SEQUENTIAL[0];
  if (pct <= 20) return SEQUENTIAL[1];
  if (pct <= 40) return SEQUENTIAL[2];
  if (pct <= 60) return SEQUENTIAL[3];
  if (pct <= 80) return SEQUENTIAL[4];
  return SEQUENTIAL[5];
}

export const CHART = {
  /** Belegung – die Hauptgröße der Seite. */
  occupancy: SERIES[0],
  /** Buchungs-Signale. */
  signal: SERIES[1],
  /** Gesamtstadt als Vergleichsfläche hinter der Auswahl. */
  reference: '#c3c2b7',
  /** Balken der Aufschlüsselung (eine Serie → eine Farbe, kein Werteverlauf). */
  bar: SERIES[0],
  /** „Merkmal unbekannt" – bewusst neutral, kein Marktsegment. */
  unknown: '#c3c2b7',

  grid: '#e1e0d9',
  axis: '#c3c2b7',
  ink: '#898781',
} as const;

/** Achsen-Defaults, damit Gitter und Beschriftung überall gleich zurücktreten. */
export const AXIS_TICK = { fontSize: 11, fill: CHART.ink } as const;

/** Durchgezogene Haarlinie. Gestrichelt ist für „Zukunft" reserviert und darf
 *  deshalb nicht zusätzlich am Gitter auftauchen. */
export const GRID_PROPS = { stroke: CHART.grid, strokeDasharray: '0' } as const;

export const FUTURE_DASH = '4 3';

/**
 * Gemeinsame Geometrie aller drei Zeitdiagramme.
 *
 * Sie müssen pixelgenau untereinander stehen, sonst zeigt der Abdeckungs-Balken
 * unter einem anderen Tag als die Kurve darüber. Früher wurde der Versatz im
 * Streifen von Hand nachgerechnet (`PLOT_INSET_LEFT = 40`); jetzt teilen sich
 * alle Diagramme diese Werte.
 */
export const Y_AXIS_WIDTH = 52;
export const CHART_MARGIN = { top: 8, right: 12, bottom: 0, left: -12 } as const;

/** Gemeinsame `syncId` — koppelt Tooltip und Zoom über die drei Zeitdiagramme. */
export const TIME_SYNC_ID = 'daten-zeitachse';

// ─── Zeitachse ────────────────────────────────────────────────────────────────

/**
 * ISO-Tag → Zeitstempel (UTC-Mitternacht).
 *
 * Bewusst UTC und bewusst zentral: `syncMethod="index"` und die Brush-Kopplung
 * vergleichen Werte über Diagramme hinweg. Würde jede Komponente ihre eigenen
 * Zeitstempel bauen, könnten Sommerzeit-Sprünge sie um eine Stunde auseinander
 * schieben.
 */
export function tsFromISO(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

export function isoFromTs(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

const WEEKDAY_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** "2026-08-20" → "20.08." – kurz genug für eine dichte Achse. */
export function formatDayShort(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

/** "2026-08-20" → "Do, 20.08.2026" – für Tooltips. */
export function formatDayLong(iso: string): string {
  const weekday = WEEKDAY_SHORT[new Date(`${iso}T00:00:00Z`).getUTCDay()];
  return `${weekday}, ${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

/**
 * Zeitstempel der Monatsersten im Bereich — als explizite Tick-Liste.
 *
 * `interval`/`minTickGap` können nur „jeden n-ten Punkt" ausblenden; daraus
 * werden bei Tagesdaten beliebige Beschriftungen wie „14.03., 28.05.".
 * Monatsgrenzen sind die einzige Einteilung, die ein Leser erwartet.
 *
 * `every` dünnt zusätzlich aus (z. B. jeden 3. Monat), wenn der Bereich lang
 * und die Karte schmal ist.
 */
export function monthStartTicks(isoDays: string[], every = 1): number[] {
  const ticks: number[] = [];
  let seen = 0;
  for (const iso of isoDays) {
    if (iso.slice(8, 10) !== '01') continue;
    if (seen % every === 0) ticks.push(tsFromISO(iso));
    seen += 1;
  }
  return ticks;
}

/** Beschriftung der Monats-Ticks: "Aug 26". */
export function formatMonthTick(ts: number): string {
  return formatMonth(isoFromTs(ts).slice(0, 7));
}

// ─── Formatierer ──────────────────────────────────────────────────────────────

export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (value == null) return '–';
  return `${(value * 100).toFixed(digits).replace('.', ',')} %`;
}

export function toPercentNumber(value: number | null | undefined): number | null {
  return value == null ? null : Math.round(value * 1000) / 10;
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
];

/** "2026-08" → "Aug 26" */
export function formatMonth(ym: string): string {
  const [year, month] = ym.split('-');
  return `${MONTH_NAMES[Number(month) - 1]} ${year.slice(2)}`;
}

export function formatNumber(value: number | null | undefined): string {
  return value == null ? '–' : value.toLocaleString('de-DE');
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
}
